"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
import { Badge } from "../ui/badge"
import { Textarea } from "../ui/textarea"
import { Button } from "../ui/button"
import { CheckCircle, XCircle, Clock, X, Check } from 'lucide-react'
import { toast } from "../hooks/use-toast"
import { format } from "date-fns"

interface LeaveReviewModalProps {
  isOpen: boolean
  onClose: () => void
  leaveRequest: any
  isViewOnly?: boolean
  onActionComplete?: (result: "approved" | "rejected", requestID: number, comment?: string) => void
}

export function LeaveReviewModal({ isOpen, onClose, leaveRequest, isViewOnly = false, onActionComplete }: LeaveReviewModalProps) {


  // Determine manager status - prefer explicit statusLabel from list, fallback to numeric mapping
  const statusLabel: "Approved" | "Rejected" | "Pending" | "Cancelled" = (() => {
    const label = (leaveRequest?.statusLabel || "").toString().toLowerCase()
    if (label) {
      if (label.includes("approve")) return "Approved"
      if (label.includes("reject")) return "Rejected"
      if (label.includes("cancel")) return "Cancelled"
    }
    const raw = leaveRequest?.rawStatus ?? leaveRequest?.status
    if (typeof raw === 'number') {
      // 5 = Pending Manager Approval
      // 6 = Pending HR Approval (means manager approved)
      // 7 = Rejected by Manager
      // 9 = Cancelled
      if (raw === 5) return "Pending"
      if (raw === 6) return "Approved"
      if (raw === 7) return "Rejected"
      if (raw === 9) return "Cancelled"
    } else if (typeof raw === 'string') {
      const s = raw.toLowerCase()
      if (s.includes("approve")) return "Approved"
      if (s.includes("reject")) return "Rejected"
      if (s.includes("cancel")) return "Cancelled"
    }
    return "Pending"
  })()

  const getApprovalBadge = (status: string) => {
    switch (status) {
      case "Approved":
        return (
          <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
            <CheckCircle className="h-3 w-3" /> Approved
          </Badge>
        )
      case "Pending":
        return (
          <Badge className="bg-orange-100 text-orange-700 flex items-center gap-1">
            <Clock className="h-3 w-3" /> Pending
          </Badge>
        )
      case "Rejected":
        return (
          <Badge className="bg-red-100 text-red-700 flex items-center gap-1">
            <XCircle className="h-3 w-3" /> Rejected
          </Badge>
        )
      // default:
      //   return <Badge variant="outline">{status}</Badge>
    }
  }

  const [managerComment, setManagerComment] = useState("")
  const [submitting, setSubmitting] = useState(false)

  // Clear manager comment each time a new request is opened
  useEffect(() => {
    if (isOpen) {
      // For pending: empty; for approved/rejected: show stored decision comment (read-only)
      if (statusLabel === "Pending") {
        setManagerComment("")
      } else {
        const decidedComment = (leaveRequest?.comments || "").trim() || (isApproved ? "Approved" : isRejected ? "Rejected" : "")
        setManagerComment(decidedComment)
      }
    }
  }, [isOpen, leaveRequest?.requestID])

  const formatDate = (iso?: string): string => {
    if (!iso) return "-"
    const d = new Date(iso)
    if (isNaN(d.getTime())) return String(iso)
    const day = String(d.getDate()).padStart(2, '0')
    const month = d.toLocaleString('en-GB', { month: 'short' })
    const year = d.getFullYear()
    return `${day}-${month}-${year}`
  }

  const formatDateTime = (iso?: string): string => {
    if (!iso) return "-"
    const d = new Date(iso)
    if (isNaN(d.getTime())) return String(iso)
    const day = String(d.getDate()).padStart(2, '0')
    const month = d.toLocaleString('en-GB', { month: 'short' })
    const year = d.getFullYear()
    const hours = String(d.getHours()).padStart(2, '0')
    const minutes = String(d.getMinutes()).padStart(2, '0')
    return `${day}-${month}-${year} ${hours}:${minutes}`
  }

  const isPending = statusLabel?.toLowerCase() === "pending"
  const isRejected = statusLabel?.toLowerCase() === "rejected"
  const isApproved = statusLabel?.toLowerCase() === "approved"
  const isCancelled = statusLabel?.toLowerCase() === "cancelled"

  const getManagerName = (): string => {

    
    if (isApproved || isRejected) {
      const managerName = leaveRequest?.managerName || leaveRequest?.approvedByName || leaveRequest?.modifiedByName    
      if (managerName && typeof managerName === 'string' && managerName !== '0' && managerName.trim() !== '') {
       
        return managerName
      }      
    } 
    
    return "-"
  }

  const managerApprovedBy = getManagerName()
  const getManagerResponseDate = () => {
    // The API returns managerResponseOn (camelCase) field
    if (leaveRequest?.managerResponseOn) {
      return formatDateTime(leaveRequest.managerResponseOn)
    }
    if (leaveRequest?.modifiedOn) {
      return formatDateTime(leaveRequest.modifiedOn)
    }
    if (leaveRequest?.approvedOn) {
      return formatDateTime(leaveRequest.approvedOn)
    }
    if (leaveRequest?.rejectedOn) {
      return formatDateTime(leaveRequest.rejectedOn)
    }
    return "-"
  }

  const managerResponseOn = getManagerResponseDate()
  const managerDecisionComment = (leaveRequest?.comments || "").trim() || (isApproved ? "Approved" : isRejected ? "Rejected" : "")

  const processAction = async (response: "Approve" | "Reject") => {
    if (!leaveRequest?.requestID && !leaveRequest?.id) {
      onClose()
      return
    }
    setSubmitting(true)
    try {
      const payload = {
        requestID: leaveRequest.requestID || leaveRequest.id,
        response: response === "Approve" ? "Approved" : "Rejected",
        comment: managerComment || "",
        modUser: leaveRequest.modUser || 0,
      }
      await axios.post(process.env.REACT_APP_API_URL + "/api/Leave/ProcessManagerAction", payload, { withCredentials: true })

      onClose()

      setTimeout(() => {
        if (response === "Approve") {
          toast({
            title: "Leave Approved",
            description: "",
            duration: 3000,
            className: "border-green-500 text-green-800",
          })
          onActionComplete && onActionComplete("approved", payload.requestID, managerComment || "")
        } else {
          toast({
            title: "Leave Rejected",
            description: "",
            duration: 3000,
            className: "border-red-500 text-red-800",
          })
          onActionComplete && onActionComplete("rejected", payload.requestID, managerComment || "")
        }
      }, 100)
    } catch (e) {
      toast({
        title: "Error",
        description: "Failed to process leave request. Please try again.",
        duration: 3000,
        className: "border-red-500 text-red-800"
      })
    } finally {
      setManagerComment("")
      setSubmitting(false)
    }
  }

  const handleApprove = () => processAction("Approve")
  const handleReject = () => processAction("Reject")

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-full sm:max-w-lg bg-white border border-gray-200 rounded-lg shadow-lg mx-2 sm:mx-auto max-h-[90vh] flex flex-col p-0">
        {/* Loading Overlay */}
        {submitting && (
          <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-3 shadow-xl">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              <p className="text-sm font-medium text-gray-700">Processing...</p>
            </div>
          </div>
        )}
        
        {/* Fixed Header */}
        <DialogHeader className="border-b border-gray-200 pb-4 px-4 sm:px-6 pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <DialogTitle className="text-lg sm:text-xl font-bold text-gray-900">
              {leaveRequest?.requestBy || leaveRequest?.requestedBy || "Employee"}
            </DialogTitle>
            <Badge variant="outline" className="text-orange-600 border-orange-200 bg-orange-50 text-xs sm:text-sm font-medium">
              {leaveRequest?.leaveType || leaveRequest?.leaveName || "Leave"}
            </Badge>
          </div>
        </DialogHeader>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto custom-scrollbar px-4 sm:px-6 py-4">
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              <strong className="text-gray-900">Applied On:</strong>{" "}
              {leaveRequest?.appliedOn || leaveRequest?.requestDate || "-"}
            </div>

            {/* Status chip near Leave Type for Cancelled */}
            {statusLabel === "Cancelled" && (
              <Badge className="bg-yellow-100 text-yellow-700 flex items-center gap-1">
                <Clock className="h-3 w-3" /> Cancelled by Employee
              </Badge>
            )}

            {/* Manager Approval Summary */}
            <div className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-gray-50 shadow-sm">
              <div className="space-y-2">
                <div className="font-medium text-gray-900 text-sm sm:text-base flex items-center gap-2 flex-wrap">
                  Manager Approval {getApprovalBadge(statusLabel)}
                </div>
                {(isApproved || isRejected) && (
                  <>
                    <div className="text-xs sm:text-sm text-gray-600">
                      <strong className="text-gray-900">Reviewed By:</strong> {String(managerApprovedBy)}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600">
                      <strong className="text-gray-900">Reviewed On:</strong> {managerResponseOn}
                    </div>
                    <div className="text-xs sm:text-sm">
                      <strong className="text-gray-900">Comment:</strong>
                      <span className="text-gray-700 ml-1 break-all break-words whitespace-pre-wrap inline-block max-w-full">
                        {managerDecisionComment || '-'}
                      </span> 
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Original Duration and Reason */}
            <div className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-gray-50 shadow-sm">
              <div className="space-y-2">
                <div className="font-medium text-gray-900 text-sm sm:text-base">
                  Duration ({leaveRequest?.totalDays || "1"} Days)
                </div>
                <div className="text-xs sm:text-sm text-gray-600">
                  {leaveRequest?.fromDate || leaveRequest?.leaveStartDate || "-"} - {leaveRequest?.toDate || leaveRequest?.leaveEndDate || "-"}
                </div>
                <div className="text-xs sm:text-sm">
                  <strong className="text-gray-900">Reason:</strong>
                  <span className="text-gray-700 ml-1 break-all break-words whitespace-pre-wrap inline-block max-w-full">
                  {leaveRequest?.reason || "-"}
                </span>
                </div>
              </div>
            </div>

            {/* Leave Day Details */}
            {leaveRequest?.leaveDayDetails && Array.isArray(leaveRequest.leaveDayDetails) && leaveRequest.leaveDayDetails.length > 0 && (
              <div className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-gray-50 shadow-sm">
                <div className="font-medium text-gray-900 text-sm sm:text-base mb-3">
                  Leave Day Details
                </div>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md bg-white custom-scrollbar">
                  <div className="divide-y divide-gray-200">
                    {leaveRequest.leaveDayDetails.map((dayDetail: any, index: number) => {
                      // Format dayType for display
                      const formatDayType = (dayType: string) => {
                        if (!dayType) return "Full Day";
                        const lower = dayType.toLowerCase();
                        if (lower === "first-half" || lower === "firsthalf") return "First-Half";
                        if (lower === "second-half" || lower === "secondhalf") return "Second-Half";
                        return "Full Day";
                      };

                      // Format date
                      const formatDateDisplay = (dateString: string | null | undefined) => {
                        if (!dateString) return "--";
                        try {
                          const date = new Date(dateString);
                          if (isNaN(date.getTime())) return String(dateString);
                          return format(date, "dd-MM-yyyy");
                        } catch {
                          return "--";
                        }
                      };
                      
                      return (
                        <div key={index} className="p-3 hover:bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div className="text-xs sm:text-sm font-medium text-gray-900">
                              {formatDateDisplay(dayDetail.leaveDate)}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600">
                              {formatDayType(dayDetail.dayType)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Manager comment input - show only when pending and not view-only */}
            {!isViewOnly && isPending && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Comment (optional)
                  </label>
                  <span className="text-xs text-gray-500">
                    {managerComment.length}/1000
                  </span>
                </div>
                <Textarea
                  value={managerComment}
                  onChange={(e) => setManagerComment(e.target.value)}
                  placeholder="Add a comment for your decision"
                  className="min-h-[100px] bg-white border-gray-200 text-gray-900 text-sm sm:text-base"
                  maxLength={1000}
                />
              </div>
            )}
          </div>
        </div>

        {/* Fixed Buttons */}
        <div className="border-t border-gray-200 px-4 sm:px-6 py-4 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 flex-shrink-0 bg-white">
          {isViewOnly ? (
            <Button
              onClick={onClose}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 sm:px-6 sm:py-2.5 text-sm sm:text-base font-medium rounded-md shadow-sm w-full sm:w-auto"
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={submitting || (statusLabel !== "Pending")}
                className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm sm:text-base font-medium rounded-md shadow-sm w-full sm:w-auto"
              >
                <X className="h-3 w-3" />
                Reject
              </Button>
              <Button
                onClick={handleApprove}
                disabled={submitting || (statusLabel !== "Pending")}
                className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm sm:text-base font-medium rounded-md shadow-sm w-full sm:w-auto"
              >
                <Check className="h-3 w-3" />
                Approve
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}